import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as R from 'ramda';

const cardStyles = {
  boxShadow: theme => theme.shadows[5],
  display: 'flex',
  flexDirection: 'column',
  height: { md: 309, xs: 384 },
  maxWidth: { lg: 580, xs: '100%' },
  width: '100%',
};

const cardContentStyles = {
  columnGap: { sm: 2, xs: 0 },
  display: 'grid',
  gridTemplate: { sm: '20px auto 57px/1fr 1fr', xs: '20px auto 57px 57px/1fr' },
  height: '100%',
  padding: 3,
  rowGap: 2,
};

const headerStyles = {
  display: 'flex',
  gridColumn: { sm: '1/span 2', xs: '1/2' },
  justifyContent: 'space-between',
};

const chipStyles = {
  backgroundColor: 'error.dark',
  gridColumn: { sm: '1/span 2', xs: '1/2' },
  height: '20px',
  ml: 1,
  mr: 0,
};

const bodyStyles = {
  backgroundColor: 'surface.light',
  gridColumn: '1/span 2',
  px: 2,
  py: 3.5,
};

const startDateStyles = {
  backgroundColor: 'error.light',
  borderRadius: '4px',
  gridColumn: '1/2',
  gridRow: '3/4',
};

const returnDateStyles = {
  border: theme => `1px solid ${theme.palette.graphic.main}`,
  borderRadius: '4px',
  gridColumn: { sm: '2/3', xs: '1/2' },
  gridRow: { sm: '3/4', xs: '4/5' },
};

const AlertCard = ({ alertData }) => {
  const { affectedProduct, comments, endDateTime, outageCategory, startDateTime } = alertData;

  return (
    <Card sx={cardStyles}>
      <CardContent sx={cardContentStyles}>
        <Box sx={headerStyles}>
          <Typography variant="body1">Upcoming Availability Issue</Typography>
          {affectedProduct && (
            <Chip
              label={
                <Typography color="surface.white" variant="caption">
                  {affectedProduct}
                </Typography>
              }
              sx={chipStyles}
            />
          )}
        </Box>
        <Stack spacing={2.5} sx={bodyStyles}>
          <Stack spacing={0.5}>
            <Typography sx={{ fontWeight: 400 }} variant="caption">
              Outage Category
            </Typography>
            <Typography variant="subtitle2">{outageCategory}</Typography>
          </Stack>
          <Stack spacing={0.5}>
            <Typography sx={{ fontWeight: 400 }} variant="caption">
              Comment
            </Typography>
            <Typography variant="subtitle2">{comments || '--'}</Typography>
          </Stack>
        </Stack>
        <Stack alignItems="center" justifyContent="center" px={1} py={1} sx={startDateStyles}>
          <Typography variant="subtitle2">
            {format(new Date(startDateTime), 'Ppp').replaceAll(',', '')} CST
          </Typography>
          <Typography sx={{ color: 'error.dark' }} variant="body2">
            Starting
          </Typography>
        </Stack>
        <Stack alignItems="center" justifyContent="center" px={1} py={1} sx={returnDateStyles}>
          <Typography variant="subtitle2">
            {R.isNil(endDateTime)
              ? 'Unknown'
              : `${format(new Date(endDateTime), 'Ppp').replaceAll(',', '')} CST`}
          </Typography>
          <Typography variant="body2">Estimated Return Date</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
};

AlertCard.propTypes = {
  alertData: PropTypes.shape({
    affectedProduct: PropTypes.string,
    comments: PropTypes.string,
    endDateTime: PropTypes.string,
    outageCategory: PropTypes.string,
    startDateTime: PropTypes.string,
  }).isRequired,
};

export default AlertCard;
